import { DateTime } from 'luxon';

import type { Timestep } from 'app-components/utils/date';

/** Separates ISO bounds in chart `category` so bars never collide on display-only labels (e.g. two "Q1 2026" rows). */
export const STABLE_TREND_CATEGORY_SEP = '::' as const;

export function stableTrendCategoryKey(row: { startOn?: string; endOn?: string; category: string }): string {
  if (row.startOn && row.endOn) return `${row.startOn}${STABLE_TREND_CATEGORY_SEP}${row.endOn}`;
  return row.category;
}

export function parseStableTrendCategoryKey(value: string): { startOn: string; endOn: string } | null {
  const idx = value.indexOf(STABLE_TREND_CATEGORY_SEP);
  if (idx <= 0) return null;
  const startOn = value.slice(0, idx);
  const endOn = value.slice(idx + STABLE_TREND_CATEGORY_SEP.length);
  if (!DateTime.fromISO(startOn).isValid || !DateTime.fromISO(endOn).isValid) return null;
  return { startOn, endOn };
}

/** Title for the selected period (e.g. "April 2026"). */
export function formatTrendPeriodTitle(startOn: string, endOn: string, timestep: Timestep): string {
  const start = DateTime.fromISO(startOn);

  if (timestep === 'MONTH') {
    return start.toFormat('LLLL yyyy');
  }

  if (timestep === 'QUARTER') {
    const quarter = Math.floor((start.month - 1) / 3) + 1;
    return `Q${quarter} ${start.year}`;
  }

  if (timestep === 'YEAR') {
    return String(start.year);
  }

  return start.toFormat('LLLL yyyy');
}

/**
 * Category string for trend chart bars/tooltips. Quarter timestep uses `Qn yyyy` from the period bounds;
 * the report often supplies a month span (e.g. "Oct - Dec 2025") which is wrong for quarter granularity.
 */
export function trendChartCategoryLabel(
  row: { category: string; startOn?: string; endOn?: string },
  timestep: Timestep,
): string {
  if (timestep === 'QUARTER' && row.startOn && row.endOn) {
    return formatTrendPeriodTitle(row.startOn, row.endOn, 'QUARTER');
  }
  return row.category;
}

/** Subheader range like "4/1/2026 - 4/14/2026". */
export function formatTrendDateRange(startOn: string, endOn: string): string {
  const start = DateTime.fromISO(startOn);
  const end = DateTime.fromISO(endOn);
  return `${start.toFormat('M/d/yyyy')} - ${end.toFormat('M/d/yyyy')}`;
}

/** Compare tooltip period label — e.g. "Apr 1 - 14, 2026" or "Apr 1, 2026". */
export function formatCompareTooltipPeriodRange(startOn: string, endOn: string): string {
  if (!startOn || !endOn) return '';
  const start = DateTime.fromISO(startOn);
  const end = DateTime.fromISO(endOn);
  if (!start.isValid || !end.isValid) return '';
  if (startOn === endOn) {
    return start.toFormat('LLL d, yyyy');
  }
  if (start.year === end.year && start.month === end.month) {
    return `${start.toFormat('LLL d')} - ${end.toFormat('d, yyyy')}`;
  }
  return `${start.toFormat('LLL d, yyyy')} - ${end.toFormat('LLL d, yyyy')}`;
}

/**
 * Trend chart category-axis tick label: show year only on January ("Jan 2026"); other months use
 * short month ("May"). Also handles quarter remap ("Q1..Q4") and an incomplete-period `*` marker on
 * the last category. Pure/framework-agnostic — shared by the AMCharts and ECharts SpendingTrendBarChart
 * implementations so both render identical axis labels.
 */
export function formatSpendingTrendCategoryAxisLabel(
  labelText: string,
  lastCategoryLabel: string | null | undefined,
  enableQuarterLabelRemap: boolean | undefined,
  axisCategoryKey: string | null | undefined,
  categoryTimestep: Timestep | undefined,
): string {
  if (labelText == null || typeof labelText !== 'string') return labelText;
  const trimmed = labelText.trim();
  const stableParsed = parseStableTrendCategoryKey(trimmed.replace(/\*$/, ''));
  if (stableParsed && categoryTimestep) {
    const inner = formatTrendPeriodTitle(stableParsed.startOn, stableParsed.endOn, categoryTimestep);
    return formatSpendingTrendCategoryAxisLabel(
      inner,
      lastCategoryLabel,
      enableQuarterLabelRemap,
      axisCategoryKey ?? trimmed,
      categoryTimestep,
    );
  }
  const m = trimmed.match(/^(.+?)\s+(\d{4})$/);
  const normalizedLast = String(lastCategoryLabel ?? '')
    .trim()
    .replace(/\*$/, '');
  const axisKey =
    axisCategoryKey != null && axisCategoryKey !== '' ? String(axisCategoryKey).trim().replace(/\*$/, '') : '';
  const identityForIncomplete = axisKey || trimmed.replace(/\*$/, '');
  const withIncompleteMarker = (value: string): string => {
    if (typeof value !== 'string') return value;
    if (!normalizedLast || identityForIncomplete !== normalizedLast) return value;
    return value.endsWith('*') ? value : `${value}*`;
  };

  // Categories like "Q1 2026" (breakdown drawer quarter mode) must not fall through to the generic
  // month branch, which would drop the year. Show a short year on Q1 only so each year is distinct.
  const quarterCategory = trimmed.replace(/\*$/, '').match(/^Q([1-4])\s+(\d{4})$/);
  if (quarterCategory) {
    const qNum = Number(quarterCategory[1]);
    const yy = String(Number(quarterCategory[2]) % 100).padStart(2, '0');
    if (qNum === 1) {
      return withIncompleteMarker(`Q1 '${yy}`);
    }
    return withIncompleteMarker(`Q${qNum}`);
  }

  // Quarter view labels from report ranges often start with the quarter's first month
  // (e.g. "Apr - Jun", "Jan 2026", "Apr - Apr"). Convert those to Q1..Q4.
  const firstMonthMatch = trimmed.match(/^([A-Za-z]{3,9})/);
  if (enableQuarterLabelRemap && firstMonthMatch) {
    const firstMonth = firstMonthMatch[1].toLowerCase().slice(0, 3);
    const monthNumByAbbrev: Record<string, number> = {
      jan: 1,
      feb: 2,
      mar: 3,
      apr: 4,
      may: 5,
      jun: 6,
      jul: 7,
      aug: 8,
      sep: 9,
      oct: 10,
      nov: 11,
      dec: 12,
    };
    const monthNum = monthNumByAbbrev[firstMonth];
    // Only quarter start months should be remapped.
    if (monthNum === 1 || monthNum === 4 || monthNum === 7 || monthNum === 10) {
      const quarter = Math.floor((monthNum - 1) / 3) + 1;
      const yearMatch = trimmed.match(/(\d{4})/);
      if (quarter === 1 && yearMatch) {
        const yy = String(Number(yearMatch[1]) % 100).padStart(2, '0');
        return withIncompleteMarker(`Q1 '${yy}`);
      }
      return withIncompleteMarker(`Q${quarter}`);
    }
  }
  if (!m) return withIncompleteMarker(labelText);
  const monthPart = m[1].trim();
  const year = m[2];
  if (/^jan/i.test(monthPart)) {
    return withIncompleteMarker(`Jan ${year}`);
  }
  if (categoryTimestep === 'MONTH') {
    const lower = monthPart.toLowerCase();
    const abb = lower.slice(0, 3);
    return withIncompleteMarker(abb.charAt(0).toUpperCase() + abb.slice(1));
  }
  return withIncompleteMarker(monthPart.charAt(0).toUpperCase() + monthPart.slice(1).toLowerCase());
}
